import {
  createCalendar,
  type DateValue,
  getLocalTimeZone,
  today
} from "@internationalized/date"
import { type CalendarAria, useCalendar } from "@react-aria/calendar"
import { useLocale } from "@react-aria/i18n"
import { useCalendarState } from "@react-stately/calendar"
import { useEffect, useRef, useState } from "react"

import { Button } from "../Button"
import { Text } from "../Text"
import styles from "./Calendar.module.css"
import { CalendarGrid } from "./CalendarGrid"

/* Private component exposing implementation details like using react-aria dates. Don't use as is! */

export interface CalendarProps {
  minValue?: "today"
  value: DateValue | undefined
  onValueChange: (value: DateValue) => void
}

export function Calendar(props: CalendarProps) {
  const { value, minValue, onValueChange } = props

  const [focusedValue, setFocusedValue] = useState(value)

  // When the value changes, update the focused value.
  useEffect(() => {
    setFocusedValue(value)
  }, [value])

  const hookProps = {
    value,
    onChange: onValueChange,
    focusedValue,
    onFocusChange: setFocusedValue
  }

  const { locale } = useLocale()

  const state = useCalendarState({
    ...hookProps,
    locale,
    createCalendar,
    minValue: getAriaMinValue(minValue)
  })

  const { calendarProps, prevButtonProps, nextButtonProps, title } =
    useCalendar(hookProps, state)

  return (
    <div {...calendarProps} className={styles.calendar}>
      <div className={styles.header}>
        <HeaderButton buttonProps={prevButtonProps} icon="arrowBack" />
        <Text variant="h4" color="grey12" noWrap>
          {title}
        </Text>
        <HeaderButton buttonProps={nextButtonProps} icon="arrowForward" />
      </div>
      <CalendarGrid state={state} />
    </div>
  )
}

function getAriaMinValue(minValue: "today" | undefined) {
  switch (minValue) {
    case "today":
      return today(getLocalTimeZone())
    case undefined:
      return undefined
  }
}

interface HeaderButtonProps {
  buttonProps: CalendarAria["prevButtonProps"]
  icon: "arrowBack" | "arrowForward"
}

function HeaderButton(props: HeaderButtonProps) {
  // Note: The react-aria's props are meant to be used with their own
  // button component so filter out unknown props and avoid warnings.
  const {
    buttonProps: { isDisabled, onPress, onFocusChange, ...buttonProps },
    icon
  } = props

  const ref = useRef<HTMLButtonElement>(null)

  return (
    <Button
      {...buttonProps}
      ref={ref}
      disabled={isDisabled}
      color="grey"
      size="medium"
      startIcon={icon}
      onClick={(event) => {
        const target = ref.current
        if (!target) return

        onPress?.({
          ...event,
          type: "press",
          pointerType: "mouse",
          target,
          continuePropagation: () => undefined,
          x: 0,
          y: 0
        })
      }}
    />
  )
}
